import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import type { ReactNode } from "react";

// Module-scope memo: Stripe.js must only be loaded once per page.
// ponytail: keyed by the single publishable key from env (constant at runtime);
// a key change would need a reload, which never happens in a static build.
let stripePromise: Promise<Stripe | null> | null = null;
function getStripe(publishableKey: string): Promise<Stripe | null> {
  if (!stripePromise) stripePromise = loadStripe(publishableKey);
  return stripePromise;
}

export interface StripeProviderProps {
  publishableKey: string | undefined;
  clientSecret: string;
  children: ReactNode;
}

/**
 * StripeProvider loads Stripe.js once and renders <Elements> for the given
 * client secret. With no publishable key it renders a friendly fallback so the
 * static build/preview works without secrets.
 */
export function StripeProvider({ publishableKey, clientSecret, children }: StripeProviderProps) {
  if (!publishableKey) {
    return (
      <p className="portal-notice" data-lumora-stripe-missing>
        Online payment is unavailable right now (no Stripe key configured). Please contact us to
        complete payment.
      </p>
    );
  }

  return (
    <Elements stripe={getStripe(publishableKey)} options={{ clientSecret }}>
      {children}
    </Elements>
  );
}
